import React, { useEffect, useState } from "react";
import { Button } from "./ui/button";
import { Smartphone, Download, Share, Plus, Check } from "lucide-react";

// Detects platform and shows the right install UX:
// - Android/Chromium with beforeinstallprompt → in-page install button
// - iOS Safari → explicit "Aggiungi a Home" instructions
// - Desktop browsers with install prompt → install button
// Hides itself if the app is already installed (display-mode: standalone).
export default function InstallAppSection() {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [isStandalone, setIsStandalone] = useState(false);
  const [installed, setInstalled] = useState(false);
  const [platform, setPlatform] = useState("desktop"); // "ios" | "android" | "desktop"

  useEffect(() => {
    const ua = window.navigator.userAgent || "";
    const iOS = /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
    const android = /Android/i.test(ua);
    setPlatform(iOS ? "ios" : android ? "android" : "desktop");

    const standalone =
      window.matchMedia("(display-mode: standalone)").matches ||
      window.navigator.standalone === true;
    setIsStandalone(standalone);

    const onBip = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };
    const onInstalled = () => {
      setInstalled(true);
      setDeferredPrompt(null);
    };
    window.addEventListener("beforeinstallprompt", onBip);
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", onBip);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  const handleInstall = async () => {
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    const choice = await deferredPrompt.userChoice;
    if (choice?.outcome === "accepted") setInstalled(true);
    setDeferredPrompt(null);
  };

  if (isStandalone || installed) return null;

  return (
    <section className="max-w-7xl mx-auto px-4 py-10" data-testid="install-app-section">
      <div className="relative overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-sm grid md:grid-cols-[1.2fr_1fr] gap-0">
        {/* Decorative side */}
        <div className="relative bg-gradient-to-br from-[#1d3d3a] via-[#244c48] to-[#2d5350] p-8 md:p-10 text-white overflow-hidden">
          <div className="absolute -top-10 -right-10 w-56 h-56 rounded-full bg-[#e86a24]/20 blur-3xl" />
          <div className="absolute -bottom-12 -left-12 w-64 h-64 rounded-full bg-white/5 blur-2xl" />
          <div className="relative">
            <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur px-3 py-1 rounded-full text-xs font-bold mb-3">
              <Smartphone className="w-3 h-3" /> App mobile
            </div>
            <h2 className="text-3xl md:text-4xl font-black leading-tight">
              Porta il marketplace<br />
              <span className="text-[#e86a24]">in tasca.</span>
            </h2>
            <p className="mt-3 text-slate-200 text-sm md:text-base max-w-md">
              Installa SAN MARINO MARKET sul tuo telefono come una vera app: notifiche, accesso rapido dalla home, niente browser.
              <strong className="block mt-1 text-white">Gratis · Nessun App Store · 5 secondi</strong>
            </p>

            {/* Direct install button (Android/Desktop with prompt) */}
            {deferredPrompt && (
              <Button
                onClick={handleInstall}
                size="lg"
                className="mt-5 bg-[#e86a24] hover:bg-[#c8561a] rounded-full shadow-lg shadow-orange-500/30"
                data-testid="install-app-button"
              >
                <Download className="w-5 h-5 mr-1.5" /> Installa l'app
              </Button>
            )}
          </div>
        </div>

        {/* Instructions side */}
        <div className="p-6 md:p-8 bg-white">
          <h3 className="text-sm font-black tracking-widest text-[#e86a24] mb-4">COME INSTALLARE</h3>

          {platform === "ios" ? (
            <Steps
              steps={[
                { icon: Share, text: "Apri il sito su Safari e tocca il pulsante Condividi in basso" },
                { icon: Plus, text: 'Scorri e seleziona "Aggiungi alla schermata Home"' },
                { icon: Check, text: 'Tocca "Aggiungi". L\'icona apparirà sulla tua Home' },
              ]}
              note="iPhone / iPad (Safari)"
            />
          ) : platform === "android" ? (
            <Steps
              steps={[
                { icon: Download, text: 'Tocca il pulsante "Installa l\'app" qui a sinistra' },
                {
                  icon: Plus,
                  text: 'Oppure dal menu ⋮ di Chrome scegli "Installa app" / "Aggiungi a schermata Home"',
                },
                { icon: Check, text: "Conferma. L'icona apparirà tra le tue app" },
              ]}
              note="Android (Chrome / Edge)"
            />
          ) : (
            <Steps
              steps={[
                { icon: Download, text: 'Clicca "Installa l\'app" qui a sinistra' },
                {
                  icon: Plus,
                  text: 'In alternativa: icona "Installa" nella barra degli indirizzi del browser',
                },
                { icon: Check, text: "L'app si aprirà in una sua finestra dedicata" },
              ]}
              note="Desktop (Chrome / Edge / Brave)"
            />
          )}

          <div className="mt-5 flex flex-wrap gap-2">
            <Badge>iOS</Badge>
            <Badge>Android</Badge>
            <Badge>Desktop</Badge>
            <Badge>Offline ready</Badge>
          </div>
        </div>
      </div>
    </section>
  );
}

function Steps({ steps, note }) {
  return (
    <>
      <p className="text-xs text-slate-500 font-semibold mb-3">{note}</p>
      <ol className="space-y-3">
        {steps.map((s, i) => {
          const Icon = s.icon;
          return (
            <li key={i} className="flex gap-3 items-start">
              <div className="shrink-0 w-9 h-9 rounded-xl bg-[#faf4e8] border border-[#e86a24]/30 flex items-center justify-center">
                <Icon className="w-4 h-4 text-[#e86a24]" />
              </div>
              <div className="flex-1 text-sm text-slate-700 leading-snug pt-1.5">
                <span className="font-bold text-[#1d3d3a] mr-1">{i + 1}.</span>
                {s.text}
              </div>
            </li>
          );
        })}
      </ol>
    </>
  );
}

function Badge({ children }) {
  return (
    <span className="text-[10px] font-bold uppercase tracking-wider text-[#1d3d3a] bg-slate-100 border border-slate-200 px-2.5 py-1 rounded-full">
      {children}
    </span>
  );
}
